import React from "react";
import { MapPin, Clock3 } from "lucide-react";
import { OriginBadge } from "../../lib/origin.jsx";
import { formatPeso, cn } from "../../lib/utils";
import { nActions } from "../../lib/opportunityApi";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Card({ label, value, hint, testid, tone }) {
  return (
    <div data-testid={testid} className="bg-surface px-3 py-2.5">
      <div className="kpi-label text-[10px]">{label}</div>
      <div className={cn("kpi-value text-lg mt-0.5", tone)}>{value}</div>
      {hint && <div className="text-[10.5px] text-muted mt-0.5 leading-snug">{hint}</div>}
    </div>
  );
}

// Key figures of the drill-down: the total, its risk-weighted value, concentration and the move since the last cycle.
export function OpportunityHead({ f, origin }) {
  const c = f.change || {};
  const d = c.available ? c.delta : null;
  return (
    <section data-testid="opp-head" className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1" data-testid="opp-cycle-at"><Clock3 size={11} /> Latest cycle · {when(f.cycle_at)}</span>
        {f.branch_scope && (
          <span data-testid="opp-branch-scope" className="text-[9.5px] uppercase tracking-widest font-semibold border border-primary/30 text-primary rounded-sm px-1.5 py-0.5 inline-flex items-center gap-1">
            <MapPin size={9} /> {f.branch_name || f.branch_scope} only
          </span>
        )}
        {origin && <OriginBadge {...origin} testid="opp-origin" />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
        <Card testid="opp-card-total" label="Today's Opportunity" value={formatPeso(f.total, { compact: true })} hint={nActions(f.count)} tone="text-primary" />
        <Card testid="opp-card-risk" label="Risk-weighted" value={formatPeso(f.risk_weighted, { compact: true })} hint="each action × its confidence" />
        <Card testid="opp-card-half" label="Half the total" value={nActions(f.concentration.actions_for_half)} hint={`top 5 = ${f.concentration.top5_share}%`} />
        <Card testid="opp-card-change" label="Since last cycle" hint={c.available ? `${c.delta_pct > 0 ? "+" : ""}${c.delta_pct}% · ${c.count_before} → ${c.count_now} actions` : "first recorded cycle"}
              value={d === null ? "—" : `${d > 0 ? "+" : d < 0 ? "−" : ""}${formatPeso(Math.abs(d), { compact: true })}`}
              tone={d > 0 ? "text-success" : d < 0 ? "text-critical" : undefined} />
      </div>
    </section>
  );
}
